import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeals } from '../context/DataContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Check, ClipboardList, GripVertical, CalendarDays, UserRound, Tag } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { fmtDate } from '../lib/format';

const emptyTask = () => ({ id: 'ptk_' + Math.random().toString(36).slice(2, 10), title: '', due_date: '', assignee_id: '', assignee_name: '', client_ids: [], done: false });

// Tags a single task/item to one or more clients — independent of the project's own client
// (which may not have one at all). Used inline in both the create dialog and the card.
const TaskClientTagger = ({ clients, selectedIds, onChange, testId }) => {
  const toggle = (cid) => onChange(selectedIds.includes(cid) ? selectedIds.filter((x) => x !== cid) : [...selectedIds, cid]);
  const names = selectedIds.map((id) => clients.find((c) => c.id === id)?.name).filter(Boolean);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`shrink-0 flex items-center gap-1 ${selectedIds.length ? 'text-pink-600' : 'text-slate-300 hover:text-pink-600'}`}
          title="tag a client to this item"
          data-testid={testId}
        >
          <Tag className="w-3.5 h-3.5" />
          {names.length > 0 && <span className="text-[10px] font-medium truncate max-w-[70px]">{names.join(', ')}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-medium mb-1.5">tag client(s)</div>
        <div className="max-h-40 overflow-y-auto space-y-0.5">
          {clients.length === 0 && <div className="text-xs text-slate-400 italic px-1">no clients yet</div>}
          {clients.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-slate-100 cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggle(c.id)} className="accent-pink-500" />
              <span className="truncate">{c.logo || '📦'} {c.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// -----------------------------------------------------------------------------
// New / edit project dialog
// -----------------------------------------------------------------------------
const ProjectDialog = ({ open, onOpenChange, editing, clients, projectTypes, directory, onSaved, onCreatedType }) => {
  const empty = { client_id: '', project_type_id: '', name: '', notes: '', tasks: [] };
  const [form, setForm] = useState(empty);
  const [newTypeName, setNewTypeName] = useState('');
  const [creatingType, setCreatingType] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        client_id: editing.client_id || '',
        project_type_id: editing.project_type_id || '',
        name: editing.name || '',
        notes: editing.notes || '',
        tasks: (editing.tasks || []).map((t) => ({ client_ids: [], ...t })),
      } : { ...empty, tasks: [emptyTask()] });
      setNewTypeName('');
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addTask = () => set('tasks', [...(form.tasks || []), emptyTask()]);
  const removeTask = (id) => set('tasks', form.tasks.filter((t) => t.id !== id));
  const patchTask = (id, patch) => set('tasks', form.tasks.map((t) => t.id === id ? { ...t, ...patch } : t));

  const createType = async () => {
    const nm = newTypeName.trim();
    if (!nm) return;
    setCreatingType(true);
    try {
      const { data } = await api.post('/project-types', { name: nm });
      onCreatedType?.(data);
      set('project_type_id', data.id);
      setNewTypeName('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to save type'); }
    finally { setCreatingType(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/projects/${editing.id}`, form);
        onSaved(data, 'update');
      } else {
        const { data } = await api.post('/projects', form);
        onSaved(data, 'create');
      }
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? 'edit project' : 'new project'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client <span className="text-slate-400 text-[11px]">(optional)</span></Label>
              <Select value={form.client_id || 'none'} onValueChange={(v) => set('client_id', v === 'none' ? '' : v)} disabled={!!editing}>
                <SelectTrigger className="mt-1" data-testid="project-client-select"><SelectValue placeholder="no client…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— no client —</SelectItem>
                  {clients.filter((c) => (c.status ?? 'active') !== 'ended').map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project type</Label>
              <Select value={form.project_type_id || 'none'} onValueChange={(v) => {
                const type_id = v === 'none' ? '' : v;
                set('project_type_id', type_id);
                // If we're creating a project (no editing) and the current task list is empty
                // or only has one blank task, seed from the type's default_tasks template.
                if (!editing && type_id) {
                  const pt = projectTypes.find((p) => p.id === type_id);
                  const defaults = pt?.default_tasks || [];
                  const currentBlank = (form.tasks || []).every((t) => !(t.title || '').trim());
                  if (defaults.length && currentBlank) {
                    set('tasks', defaults.map((t, i) => ({
                      id: 'ptk_' + Math.random().toString(36).slice(2, 10),
                      title: t.title || '',
                      description: t.description || '',
                      due_date: null,
                      assignee_id: '',
                      done: false,
                      position: i,
                    })));
                  }
                }
              }}>
                <SelectTrigger className="mt-1" data-testid="project-type-select"><SelectValue placeholder="pick a type…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {projectTypes.map((pt) => <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="mt-1 flex items-center gap-1">
                <Input
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createType(); } }}
                  placeholder="or create a new type…"
                  className="h-8 text-xs"
                  data-testid="project-new-type-input"
                />
                <Button size="sm" variant="ghost" onClick={createType} disabled={!newTypeName.trim() || creatingType} className="h-8 text-xs">
                  save
                </Button>
              </div>
              {form.project_type_id && (
                <button
                  type="button"
                  onClick={async () => {
                    const pt = projectTypes.find((p) => p.id === form.project_type_id);
                    const next = window.prompt('rename template', pt?.name || '');
                    if (!next || next.trim() === (pt?.name || '')) return;
                    try {
                      const { data } = await api.patch(`/project-types/${form.project_type_id}`, { name: next.trim() });
                      onCreatedType?.(data); // triggers parent to refresh types list
                      toast.success('renamed');
                    } catch { toast.error('rename failed'); }
                  }}
                  className="mt-1 text-[11px] text-pink-600 hover:text-pink-700 underline"
                  data-testid="project-rename-type"
                >rename this template</button>
              )}
            </div>
          </div>
          <div>
            <Label>Project name <span className="text-slate-400 text-[11px]">(optional)</span></Label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Q1 content refresh" />
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 text-[11px]">(optional)</span></Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Tasks</Label>
              <div className="flex items-center gap-1">
                {form.project_type_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[11px] text-pink-600 hover:bg-pink-50"
                    onClick={async () => {
                      try {
                        const defaults = form.tasks.filter((t) => (t.title || '').trim()).map((t, i) => ({
                          title: t.title, description: t.description || '', position: i,
                        }));
                        await api.patch(`/project-types/${form.project_type_id}`, { default_tasks: defaults });
                        toast.success('saved as template');
                      } catch { toast.error('failed to save template'); }
                    }}
                    title="save these tasks as the default template for this project type"
                    data-testid="project-save-template"
                  >save as template</Button>
                )}
                <Button size="sm" variant="ghost" onClick={addTask} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" /> add task</Button>
              </div>
            </div>
            <div className="space-y-2">
              {form.tasks.map((t, i) => (
                <div key={t.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <Input value={t.title} onChange={(e) => patchTask(t.id, { title: e.target.value })} placeholder={`task ${i + 1}`} className="h-8 text-xs" />
                  </div>
                  <div className="col-span-2">
                    <Input type="date" value={t.due_date || ''} onChange={(e) => patchTask(t.id, { due_date: e.target.value })} className="h-8 text-xs" />
                  </div>
                  <div className="col-span-5 flex items-center gap-1">
                    <Select
                      value={t.assignee_id || 'none'}
                      onValueChange={(v) => patchTask(t.id, { assignee_id: v === 'none' ? '' : v, assignee_name: '' })}
                    >
                      <SelectTrigger className="h-8 text-xs flex-1 min-w-0"><SelectValue placeholder="assignee" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— unassigned —</SelectItem>
                        {directory.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} <span className="text-slate-400 text-[10px]">({u.role})</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      value={t.assignee_name || ''}
                      onChange={(e) => patchTask(t.id, { assignee_name: e.target.value, assignee_id: e.target.value ? '' : t.assignee_id })}
                      placeholder="or type"
                      className="h-8 text-xs w-16 shrink-0"
                      data-testid={`project-task-assignee-name-${t.id}`}
                    />
                    <TaskClientTagger
                      clients={clients}
                      selectedIds={t.client_ids || []}
                      onChange={(ids) => patchTask(t.id, { client_ids: ids })}
                      testId={`project-task-tag-client-${t.id}`}
                    />
                    <button onClick={() => removeTask(t.id)} className="text-slate-400 hover:text-red-600 shrink-0" title="delete task"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {form.tasks.length === 0 && <div className="text-xs text-slate-400 italic">no tasks yet — click "add task"</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white">{saving ? 'saving…' : (editing ? 'save' : 'create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Task title — click to edit inline
// -----------------------------------------------------------------------------
const TaskTitleInline = ({ task, onPatch }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title || '');
  useEffect(() => { setDraft(task.title || ''); }, [task.title]);

  const commit = () => {
    setEditing(false);
    const clean = draft.trim();
    if (clean !== (task.title || '')) onPatch({ title: clean });
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(task.title || ''); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 min-w-0 text-sm bg-transparent border-b border-pink-300 outline-none px-0"
        data-testid={`project-task-title-input-${task.id}`}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      className={`flex-1 min-w-0 truncate cursor-text ${task.done ? 'line-through text-slate-400' : 'text-slate-700'}`}
      data-testid={`project-task-title-${task.id}`}
    >
      {task.title || <span className="text-slate-400 italic">untitled task — click to name</span>}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Project card
// -----------------------------------------------------------------------------
const ProjectCard = ({ project, client, clients, projectType, directory, onPatch, onDelete, onEdit }) => {
  const done = (project.tasks || []).filter((t) => t.done).length;
  const total = (project.tasks || []).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const toggleTask = (taskId, patch) => {
    const next = (project.tasks || []).map((t) => t.id === taskId ? { ...t, ...patch } : t);
    onPatch({ tasks: next });
  };
  const quickAddTask = () => {
    onPatch({ tasks: [...(project.tasks || []), emptyTask()] });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-pink-200 transition-colors" data-testid={`project-card-${project.id}`}>
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start gap-3">
          <div className="text-2xl leading-none">{client?.logo || '📦'}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-500 truncate">{client?.name || <span className="italic text-slate-400">no client</span>}</div>
            <div className="font-semibold text-slate-800 truncate">{project.name || projectType?.name || 'untitled project'}</div>
            {projectType && project.name && <div className="text-[11px] text-slate-500 mt-0.5">{projectType.name}</div>}
          </div>
          <button onClick={() => onEdit()} className="text-[11px] text-pink-600 hover:text-pink-700 font-semibold">edit</button>
          <button onClick={() => { if (window.confirm('delete this project?')) onDelete(); }} className="text-slate-400 hover:text-red-600" title="delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-pink-500" style={{ width: `${pct}%` }} /></div>
          <div className="text-[11px] text-slate-500 tabular-nums">{done}/{total}</div>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {(project.tasks || []).map((t) => {
          const assignee = directory.find((u) => u.user_id === t.assignee_id);
          const assigneeLabel = t.assignee_name || assignee?.name || '';
          const taggedClientNames = (t.client_ids || []).map((cid) => clients.find((c) => c.id === cid)?.name).filter(Boolean);
          const overdue = !t.done && t.due_date && new Date(t.due_date) < new Date(new Date().toDateString());
          return (
            <li key={t.id} className="px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-slate-50">
              <button
                onClick={() => toggleTask(t.id, { done: !t.done })}
                className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${t.done ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 hover:border-slate-500'}`}
                title={t.done ? 'mark not done' : 'mark done'}
              >{t.done && <Check className="w-3 h-3" />}</button>
              <TaskTitleInline task={t} onPatch={(patch) => toggleTask(t.id, patch)} />
              {t.due_date && <span className={`text-[11px] shrink-0 ${overdue ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>{fmtDate(t.due_date)}</span>}
              {assigneeLabel && <span className="text-[10px] shrink-0 bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 truncate max-w-[90px]" title={assigneeLabel}>{assigneeLabel.split(' ')[0]}</span>}
              {taggedClientNames.length > 0 && (
                <span className="text-[10px] shrink-0 bg-pink-50 text-pink-700 border border-pink-200 rounded-full px-2 py-0.5 truncate max-w-[100px]" title={taggedClientNames.join(', ')} data-testid={`project-task-client-chip-${t.id}`}>
                  {taggedClientNames.join(', ')}
                </span>
              )}

              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-slate-300 hover:text-pink-600 shrink-0" title="set date" data-testid={`project-task-date-icon-${t.id}`}>
                    <CalendarDays className="w-3.5 h-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="end">
                  <Input
                    type="date"
                    value={t.due_date || ''}
                    onChange={(e) => toggleTask(t.id, { due_date: e.target.value })}
                    className="h-8 text-xs"
                    data-testid={`project-task-date-input-${t.id}`}
                  />
                  {t.due_date && (
                    <button onClick={() => toggleTask(t.id, { due_date: '' })} className="text-[11px] text-slate-400 hover:text-red-600 mt-1.5">clear date</button>
                  )}
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-slate-300 hover:text-pink-600 shrink-0" title="assign to" data-testid={`project-task-assignee-icon-${t.id}`}>
                    <UserRound className="w-3.5 h-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <Input
                    value={t.assignee_name || ''}
                    onChange={(e) => toggleTask(t.id, { assignee_name: e.target.value, assignee_id: e.target.value ? '' : t.assignee_id })}
                    placeholder="type a name…"
                    className="h-8 text-xs"
                    data-testid={`project-task-assignee-input-${t.id}`}
                  />
                  <div className="text-[10px] text-slate-400 mt-1.5 mb-1">or pick from the team</div>
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    <button
                      onClick={() => toggleTask(t.id, { assignee_id: '', assignee_name: '' })}
                      className="w-full text-left text-xs px-2 py-1 rounded hover:bg-slate-100 text-slate-500"
                    >— unassigned —</button>
                    {directory.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => toggleTask(t.id, { assignee_id: u.user_id, assignee_name: '' })}
                        className={`w-full text-left text-xs px-2 py-1 rounded hover:bg-slate-100 ${t.assignee_id === u.user_id ? 'text-pink-600 font-medium' : 'text-slate-600'}`}
                      >{u.name}</button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <TaskClientTagger
                clients={clients}
                selectedIds={t.client_ids || []}
                onChange={(ids) => toggleTask(t.id, { client_ids: ids })}
                testId={`project-task-tag-client-icon-${t.id}`}
              />
            </li>
          );
        })}
        {(project.tasks || []).length === 0 && <li className="px-4 py-4 text-xs text-slate-400 italic">no tasks yet</li>}
      </ul>
      <div className="px-4 py-2 border-t border-slate-100">
        <button
          onClick={quickAddTask}
          className="inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700 font-medium"
          data-testid={`project-quick-add-task-${project.id}`}
        >
          <Plus className="w-3.5 h-3.5" /> quick add task
        </button>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const Projects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clientFilter, setClientFilter] = useState('all');

  const load = async () => {
    try {
      const [pj, pt, cl, dir] = await Promise.all([
        api.get('/projects'),
        api.get('/project-types'),
        api.get('/clients'),
        api.get('/team/directory'),
      ]);
      setProjects(pj.data || []);
      setProjectTypes(pt.data || []);
      setClients(cl.data || []);
      setDirectory(dir.data || []);
    } catch { toast.error('failed to load projects'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const typeMap = useMemo(() => Object.fromEntries(projectTypes.map((t) => [t.id, t])), [projectTypes]);
  const visible = useMemo(() => (clientFilter === 'all' ? projects : projects.filter((p) => p.client_id === clientFilter)), [projects, clientFilter]);

  const onSaved = (data, action) => {
    setProjects((prev) => action === 'update' ? prev.map((p) => p.id === data.id ? data : p) : [data, ...prev]);
  };
  const patchProject = async (id, patch) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, patch);
      setProjects((prev) => prev.map((p) => p.id === id ? data : p));
    } catch { toast.error('update failed'); }
  };
  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch { toast.error('delete failed'); }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-pink-500" /> projects
          </h1>
          <p className="text-sm text-slate-500 mt-1">one board per client project — assign tasks to your team or your talent.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-10 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-1.5" /> new project
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">no projects yet — click new project to start</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              client={clientMap[p.client_id]}
              clients={clients}
              projectType={typeMap[p.project_type_id]}
              directory={directory}
              onPatch={(patch) => patchProject(p.id, patch)}
              onDelete={() => deleteProject(p.id)}
              onEdit={() => { setEditing(p); setDialogOpen(true); }}
            />
          ))}
        </div>
      )}

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        clients={clients}
        projectTypes={projectTypes}
        directory={directory}
        onSaved={onSaved}
        onCreatedType={(t) => setProjectTypes((prev) => {
          const idx = prev.findIndex((p) => p.id === t.id);
          if (idx >= 0) { const next = [...prev]; next[idx] = t; return next; }
          return [...prev, t];
        })}
      />
    </div>
  );
};

export default Projects;
